// src/admin/AdminShared.jsx
// Shared UI primitives — all Tailwind, matching the app's existing design tokens:
// orange-400/600 brand, white cards, gray-200 borders, same input/button styles as Login.js

import React from 'react';

// ─── Colour helpers ───────────────────────────────────────────────────────────
export const TONE_STYLE = {
  Empathetic: { bg: 'bg-green-100',  text: 'text-green-700'  },
  Clinical:   { bg: 'bg-blue-100',   text: 'text-blue-700'   },
  Rushed:     { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  Confused:   { bg: 'bg-red-100',    text: 'text-red-600'    },
  Confusing:  { bg: 'bg-red-100',    text: 'text-red-600'    },
};
export const TONE_HEX = {
  Empathetic: '#16a34a', Clinical: '#2563eb',
  Rushed: '#ca8a04',     Confused: '#dc2626', Confusing: '#9f1239',
};

export function empHex(s) {
  return s >= 7 ? '#16a34a' : s >= 5 ? '#ca8a04' : '#dc2626';
}
export function empClass(s) {
  return s >= 7 ? 'text-green-600' : s >= 5 ? 'text-yellow-600' : 'text-red-500';
}
export function likertHex(s) {
  return s >= 4 ? '#16a34a' : s >= 3 ? '#ca8a04' : '#dc2626';
}

// ─── Full-page spinner ────────────────────────────────────────────────────────
export function PageSpinner() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400 text-sm">Loading data from API…</p>
    </div>
  );
}

// ─── Error box ────────────────────────────────────────────────────────────────
export function ErrorBox({ message }) {
  const isCORS = message && (message.includes('CORS') || message.includes('Network') || message.includes('network'));
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm space-y-3">
      <p className="font-bold text-red-700 text-base">❌ Could not load data from API</p>

      <div className="bg-white border border-red-100 rounded-lg p-3">
        <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-1">Error details</p>
        <p className="text-red-600 text-xs font-mono break-all whitespace-pre-wrap">{message}</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="font-semibold text-amber-800 mb-2">
          {isCORS ? '🚫 This is a CORS error — the server is blocking the browser request.' : '🔍 Diagnostic steps:'}
        </p>
        {isCORS ? (
          <div className="space-y-2 text-xs text-amber-700">
            <p>The Flask server at <code className="bg-amber-100 px-1 rounded">consult.conversationaily.ai</code> must allow requests from <code className="bg-amber-100 px-1 rounded">{window.location.origin}</code>.</p>
            <p className="font-semibold">Ask your backend team to add this to the Flask app:</p>
            <pre className="bg-amber-100 rounded p-2 overflow-x-auto text-xs">{`from flask_cors import CORS
CORS(app)  # allow all origins

# OR just for this endpoint:
@app.after_request
def cors(r):
    r.headers['Access-Control-Allow-Origin'] = '*'
    r.headers['Access-Control-Allow-Headers'] = 'Content-Type,Authorization'
    return r`}</pre>
          </div>
        ) : (
          <ul className="text-xs text-amber-700 list-disc list-inside space-y-1">
            <li>Open browser DevTools → Network tab → find the failed request and check the response</li>
            <li>Visit <strong>/admin-debug</strong> (add the debug route) to see the raw API response</li>
            <li>Check that the API URL is correct and the server is running</li>
            <li>Check the browser Console for <code>[AdminDashboard]</code> log messages</li>
          </ul>
        )}
      </div>

      <p className="text-xs text-red-300">
        API: https://consult.conversationaily.ai/api/pre-scenario/data
      </p>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, accentClass }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-1 shadow-sm">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{label}</span>
      <span className={`text-3xl font-bold leading-none ${accentClass || 'text-gray-800'}`}>{value}</span>
      {sub && <span className="text-xs text-gray-400 mt-1">{sub}</span>}
    </div>
  );
}

// ─── Horizontal bar ───────────────────────────────────────────────────────────
export function HBar({ label, value, max, hex, truncate = true }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-sm mb-1 gap-2">
        <span className={`text-gray-600 ${truncate ? 'truncate' : ''}`} style={truncate ? { maxWidth: '78%' } : {}}>
          {label}
        </span>
        <span className="font-semibold text-gray-700 flex-shrink-0">{value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: hex || '#f97316' }} />
      </div>
    </div>
  );
}

// ─── Score bar (inline with number) ──────────────────────────────────────────
export function ScoreBar({ value, max, hexFn }) {
  if (value == null) return <span className="text-xs text-gray-300">—</span>;
  const pct = Math.round((value / max) * 100);
  const hex = hexFn(value);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: hex }} />
      </div>
      <span className="text-xs font-bold w-7 text-right tabular-nums" style={{ color: hex }}>
        {Number.isInteger(value) ? value : value.toFixed(1)}
      </span>
    </div>
  );
}

// ─── Tone badge ───────────────────────────────────────────────────────────────
export function ToneBadge({ tone }) {
  const s = TONE_STYLE[tone] || { bg: 'bg-gray-100', text: 'text-gray-500' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${s.bg} ${s.text}`}>
      {tone || '—'}
    </span>
  );
}

// ─── Generic badge ────────────────────────────────────────────────────────────
export function Badge({ children, type = 'neutral' }) {
  const styles = {
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    danger:  'bg-red-100 text-red-600',
    info:    'bg-blue-100 text-blue-700',
    neutral: 'bg-gray-100 text-gray-500',
    orange:  'bg-orange-100 text-orange-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${styles[type] || styles.neutral}`}>
      {children}
    </span>
  );
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────
export function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

// ─── Card section label ───────────────────────────────────────────────────────
export function CardLabel({ children }) {
  return (
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">{children}</p>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ children, onClose, wide = false }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-2xl shadow-2xl my-8 w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'}`}>
        {children}
      </div>
    </div>
  );
}

// ─── Modal header ─────────────────────────────────────────────────────────────
export function ModalHeader({ title, sub, onClose }) {
  return (
    <div className="flex items-start justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
      <div>
        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        {sub && <p className="text-xs text-gray-400 mt-0.5 font-mono">{sub}</p>}
      </div>
      <button onClick={onClose} className="text-gray-300 hover:text-gray-500 text-3xl leading-none ml-4 flex-shrink-0">
        &times;
      </button>
    </div>
  );
}

// ─── Meta grid cell ───────────────────────────────────────────────────────────
export function MetaCell({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</div>
      <div className="font-semibold text-sm text-gray-700 break-words">{String(value ?? '—')}</div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ page, total, perPage, onChange }) {
  const pages = Math.ceil(total / perPage);
  const from  = page * perPage + 1;
  const to    = Math.min((page + 1) * perPage, total);
  return (
    <div className="flex items-center justify-between text-sm text-gray-500 mt-4">
      <span>Showing {from}–{to} of {total}</span>
      <div className="flex gap-2">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 0}
          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >← Prev</button>
        <span className="px-3 py-1.5 font-medium">{page + 1} / {pages}</span>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= pages - 1}
          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >Next →</button>
      </div>
    </div>
  );
}

// ─── Search + select filter row ───────────────────────────────────────────────
export function FilterRow({ children }) {
  return <div className="flex flex-wrap gap-3 mb-4">{children}</div>;
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
    />
  );
}

export function SelectFilter({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
    >
      {children}
    </select>
  );
}

// ─── View button ─────────────────────────────────────────────────────────────
export function ViewBtn({ onClick }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      className="text-xs px-3 py-1.5 border border-orange-400 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors font-medium whitespace-nowrap"
    >
      View →
    </button>
  );
}

// ─── Table shell ─────────────────────────────────────────────────────────────
export function TableShell({ headers, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {headers.map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">{children}</tbody>
        </table>
      </div>
    </div>
  );
}